import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiHome, FiArrowLeft, FiRefreshCw, FiMail, FiCopy } from 'react-icons/fi';
import Button from '../../components/common/Button';
import { useNotification } from '../../contexts/NotificationContext';
import { useAppConfig } from '../../contexts/AppConfigContext';

const ServerError = ({ error, errorInfo }) => {
  const navigate = useNavigate();
  const { showSuccess } = useNotification();
  const { appName } = useAppConfig();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Add a small delay to show the loading state
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleCopyError = () => {
    const errorDetails = `
Error: ${error?.message || 'Unknown error'}
Stack: ${error?.stack || 'No stack trace available'}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
    `.trim();
    
    navigator.clipboard.writeText(errorDetails).then(() => {
      showSuccess('Error details copied to clipboard');
    });
  };

  const handleReportError = () => {
    const errorDetails = `
Error: ${error?.message || 'Unknown error'}
Stack: ${error?.stack || 'No stack trace available'}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
    `.trim();
    
    const subject = encodeURIComponent(`${appName} Application Error Report`);
    const body = encodeURIComponent(`Hi Support Team,

I encountered an error while using the ${appName} application.

Error Details:
${errorDetails}

Steps to reproduce:
1. [Please describe what you were doing when the error occurred]
2. 
3. 

Additional information:
[Any other relevant information]

Thank you for your help!`);
    
    window.open(`mailto:support@passqik.com?subject=${subject}&body=${body}`);
  };

  return (
    <div className="error-page">
      <div className="error-container">
        <div className="error-illustration">
          <div className="error-code">500</div>
          <FiAlertTriangle className="error-icon" size={64} />
        </div>
        
        <div className="error-content">
          <h1 className="error-title">Something Went Wrong</h1>
          <p className="error-description">
            We're sorry, but something unexpected happened. Our team has been notified 
            and is working to fix the issue. Please try again in a few moments.
          </p>
          
          {error && (
            <div className="error-details">
              <h3>Error Details:</h3>
              <div className="error-message">
                <code>{error.message}</code>
              </div>
              {process.env.NODE_ENV === 'development' && error.stack && (
                <details className="error-stack">
                  <summary>Stack Trace (Development Only)</summary>
                  <pre>{error.stack}</pre>
                </details>
              )}
            </div>
          )}
          
          <div className="error-suggestions">
            <h3>What you can try:</h3>
            <ul>
              <li>Refresh the page to try again</li>
              <li>Check your internet connection</li>
              <li>Clear your browser cache and cookies</li>
              <li>Try again in a few minutes</li>
              <li>Contact support if the problem persists</li>
            </ul>
          </div>
          
          <div className="error-actions">
            <Button 
              variant="outline" 
              onClick={handleGoBack}
              className="action-button"
            >
              <FiArrowLeft size={16} />
              Go Back
            </Button>
            
            <Button 
              variant="primary" 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="action-button"
            >
              {isRefreshing ? (
                <div className="spinner" />
              ) : (
                <FiRefreshCw size={16} />
              )}
              {isRefreshing ? 'Refreshing...' : 'Refresh Page'}
            </Button>
            
            <Button 
              variant="secondary" 
              onClick={handleGoHome}
              className="action-button"
            >
              <FiHome size={16} />
              Go Home
            </Button>
          </div>
          
          <div className="error-support">
            <h3>Need Help?</h3>
            <div className="support-actions">
              <Button 
                variant="text" 
                onClick={handleCopyError}
                className="support-button"
              >
                <FiCopy size={16} />
                Copy Error Details
              </Button>
              
              <Button 
                variant="text" 
                onClick={handleReportError}
                className="support-button"
              >
                <FiMail size={16} />
                Report This Error
              </Button>
            </div>
          </div>
        </div>
        
        <div className="error-footer">
          <p>
            <Link to="/contact">Contact Support</Link> |{' '}
            <Link to="/help">Help Center</Link> |{' '}
            <Link to="/status">System Status</Link>
          </p>
          <p className="error-id">
            Error ID: {Date.now().toString(36).toUpperCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServerError;